/**
 * Image resize utility for RTE photo uploads
 * 
 * Provides client-side image resizing and validation using native Canvas API.
 * Always outputs JPEG for consistent S3 storage regardless of input format.
 */

/**
 * Validates an image file before processing
 * @param {File} file - The image file to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateImageFile = (file) => {
  // Check MIME type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Only JPEG and PNG images are allowed.'
    };
  }

  // Check file size (5MB = 5 * 1024 * 1024 bytes)
  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: 'File too large. Maximum size is 5MB.'
    };
  }

  return { valid: true, error: null };
};

/**
 * Resizes an image file using Canvas API
 * @param {File} file - The image file to resize
 * @param {Object} options - Resize options
 * @param {number} options.maxWidth - Maximum width (default: 1200)
 * @param {number} options.maxHeight - Maximum height (default: 1200)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {string} options.type - Output type (default: 'image/jpeg')
 * @returns {Promise<Blob>} - The resized image as a Blob
 */
export const resizeImage = (file, options = {}) => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    type = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    // Create an image element to load the file
    const img = new Image();
    
    img.onload = () => {
      try {
        // Calculate new dimensions while preserving aspect ratio
        const { width: newWidth, height: newHeight } = calculateDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        // Create canvas and context
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas dimensions
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Draw the image onto the canvas with new dimensions
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          type,
          quality
        );
      } catch (error) {
        reject(new Error(`Image processing failed: ${error.message}`));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image file'));
    };

    // Load the image from the file
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Calculate new dimensions while preserving aspect ratio
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {number} maxWidth - Maximum allowed width
 * @param {number} maxHeight - Maximum allowed height
 * @returns {Object} - { width: number, height: number }
 */
const calculateDimensions = (originalWidth, originalHeight, maxWidth, maxHeight) => {
  // If image is already smaller than max dimensions, still resize to ensure quality
  let width = originalWidth;
  let height = originalHeight;

  // Calculate scale factor to fit within max dimensions
  const widthRatio = maxWidth / width;
  const heightRatio = maxHeight / height;
  const scaleRatio = Math.min(widthRatio, heightRatio, 1);

  // Apply the scale ratio
  width = Math.round(width * scaleRatio);
  height = Math.round(height * scaleRatio);

  return { width, height };
};